import { Autocomplete, Box, Tab, Tabs, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, Redirect, useLocation } from "react-router-dom";
import { radius } from "src/design/tokens";
import { EmptyState, InlineAlert } from "src/grading/components";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { Permission } from "src/models";
import { OrganizationView } from "src/models/hierarchy";
import { useListDistrictsQuery } from "src/store/reducers/districts/districtsApiSlice";
import DistrictAdminsTab from "./DistrictAdminsTab";
import InvitationsTab from "./InvitationsTab";
import StaffTab from "./StaffTab";
import StudentsTab from "./StudentsTab";
import { PEOPLE_PATH, PeopleTab, usePeopleUrlState } from "./usePeopleUrlState";

interface PeopleTabDef {
  key: PeopleTab;
  /** Listed without edit actions (platform admins on Students). */
  readOnly: boolean;
}

/** Tabs by role (contracts/people-console.md §Tabs by role). The server is the security boundary. */
export const peopleTabsFor = (has: (permission: Permission) => boolean) => {
  const platformAdmin = has(permissions.READ_DISTRICTS);
  const tabs: PeopleTabDef[] = [];
  if (platformAdmin) tabs.push({ key: "district-admins", readOnly: false });
  if (has(permissions.READ_STAFF)) tabs.push({ key: "staff", readOnly: false });
  if (
    has(permissions.READ_SCHOOL_STUDENTS) ||
    has(permissions.READ_CLASS_STUDENTS)
  ) {
    tabs.push({ key: "students", readOnly: platformAdmin });
  }
  if (has(permissions.READ_INVITATIONS)) {
    tabs.push({ key: "invitations", readOnly: false });
  }
  return tabs;
};

const TAB_PANEL_ID = "people-tabpanel";
const tabId = (key: PeopleTab) => `people-tab-${key}`;

/** Staff, students and invitations in the viewer's scope, one tab per list the viewer may read. */
const PeoplePage = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { hasPermissions } = usePermissions();
  const url = usePeopleUrlState();

  const has = (permission: Permission) =>
    hasPermissions(permission.action, permission.subject);
  const isPlatformAdmin = has(permissions.READ_DISTRICTS);
  const tabs = peopleTabsFor(has);

  const { data: districts, isLoading: districtsLoading } =
    useListDistrictsQuery(undefined, { skip: !isPlatformAdmin });

  const title = t("people.title");
  const districtName = districts?.find((d) => d.id === url.district)?.name;

  if (tabs.length === 0) {
    return (
      <>
        <PageHeader title={title} />
        <EmptyState
          title={t("people.noAccessTitle")}
          body={t("people.noAccessBody")}
        />
      </>
    );
  }

  const current = tabs.find((tab) => tab.key === url.tab);
  if (!current) {
    // No tab yet: keep the query (a shared filtered link). A tab this viewer cannot see: its
    // filters and panel ids belong to that tab, so keep only the district.
    return (
      <Redirect
        to={
          url.tab === undefined
            ? `${PEOPLE_PATH}/${tabs[0].key}${search}`
            : url.tabHref(tabs[0].key)
        }
      />
    );
  }

  const districtPicker = isPlatformAdmin ? (
    <Autocomplete<OrganizationView>
      options={districts ?? []}
      value={districts?.find((d) => d.id === url.district) ?? null}
      onChange={(_event, district) => url.setDistrict(district?.id)}
      getOptionLabel={(district) => district.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={districtsLoading}
      loadingText={t("common.loading")}
      noOptionsText={t("picker.noMatches")}
      sx={{ width: { xs: "100%", sm: 300 } }}
      renderInput={(params) => (
        <TextField {...params} size="small" label={t("people.district")} />
      )}
    />
  ) : undefined;

  return (
    <>
      <PageHeader title={title} actions={districtPicker} />
      <Tabs
        value={current.key}
        aria-label={t("people.tabsLabel")}
        variant="scrollable"
        scrollButtons={false}
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={(theme) => ({
          minHeight: 0,
          mb: 2.5,
          p: "4px",
          width: "fit-content",
          borderRadius: `${radius.pill}px`,
          bgcolor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
          maxWidth: "100%",
          "& .MuiTabs-flexContainer": { gap: "4px" },
        })}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            id={tabId(tab.key)}
            aria-controls={TAB_PANEL_ID}
            component={Link}
            to={url.tabHref(tab.key)}
            value={tab.key}
            label={t(`people.tabs.${tab.key}`)}
            disableRipple
            sx={(theme) => ({
              minHeight: 36,
              minWidth: 0,
              px: 2,
              py: 0,
              borderRadius: `${radius.pill}px`,
              textTransform: "none",
              fontSize: 13,
              fontWeight: 700,
              color: theme.palette.text.primary,
              "&.Mui-selected": {
                backgroundColor: theme.palette.strong,
                color: theme.palette.onStrong,
                fontWeight: 800,
              },
              "&.Mui-focusVisible": {
                outline: `2px solid ${theme.palette.focusRing}`,
                outlineOffset: 2,
              },
            })}
          />
        ))}
      </Tabs>
      <Box
        role="tabpanel"
        id={TAB_PANEL_ID}
        aria-labelledby={tabId(current.key)}
      >
        {isPlatformAdmin && !url.district ? (
          <EmptyState
            title={t("people.chooseDistrictTitle")}
            body={t("people.chooseDistrictBody")}
          />
        ) : current.key === "district-admins" ? (
          <DistrictAdminsTab />
        ) : current.key === "staff" ? (
          <StaffTab districtName={districtName} />
        ) : current.key === "invitations" ? (
          <InvitationsTab districtName={districtName} />
        ) : (
          <>
            {current.readOnly ? (
              <Box sx={{ mb: 2 }}>
                <InlineAlert severity="info">
                  {t("people.studentsReadOnly")}
                </InlineAlert>
              </Box>
            ) : null}
            <StudentsTab readOnly={current.readOnly} />
          </>
        )}
      </Box>
    </>
  );
};

export default PeoplePage;
